import { getVersion } from './util';
import { ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS } from './emailTypeService';

/**
 * Diagnostics for the add-in startup sequence.
 *
 * Startup runs MSAL token acquisition -> item.saveAsync -> POST /api/link.
 * Every one of those steps used to collapse into the same
 * "Client could not get token" message, and most of them threw away the
 * underlying error, so a support screenshot could not tell them apart.
 *
 * Each failure now carries a stable code, the phase it happened in, and a
 * serialized detail block. Nothing here ever reads message content.
 */

// Stable, user-quotable codes. Never reuse or renumber one: support tickets
// and log queries refer to them.
export const StartupErrorCode = {
    MSAL_INIT: 'MM-01',       // MSAL could not be created, or onSignIn threw unexpectedly
    INTERACTIVE: 'MM-02',     // acquireTokenPopup failed for a non-popup reason (consent, conditional access)
    DIALOG_FALLBACK: 'MM-03', // popup problem, and the Office Dialog API fallback also failed
    NO_TOKEN: 'MM-04',        // silent, popup and dialog all finished without a token
    SAVE_ITEM: 'MM-05',       // Office.context.mailbox.item.saveAsync reported a failure
    AFTER_SAVE: 'MM-06',      // saveAsync succeeded but reading the item id / starting the link threw
    LINK_REQUEST: 'MM-07',    // POST /api/link never returned a result (network, 5xx, CORS)
    LINK_REJECTED: 'MM-08',   // the server returned a failure result
    REAUTH_FAILED: 'MM-09',   // the server rejected the identity token twice
    AUTH_LOOP: 'MM-10',       // the client loop breaker tripped
};

// Strings are capped individually so one huge stack or HTML error page cannot
// crowd out the rest of the report
export const MAX_VALUE_LENGTH = 1000;
// Whole-report cap, matched by the server side of /api/log/clienterror
export const MAX_DIAGNOSTICS_LENGTH = 8000;

export interface StartupDiagnostics {
    code: string;
    phase: string;
    version: string;
    at: string;
    host: { [key: string]: any };
    detail?: any;
}

export const truncate = (value: string, maxLength: number): string =>
    typeof value === 'string' && value.length > maxLength
        ? value.substr(0, maxLength) + '...[truncated]'
        : value;

export const safeStringify = (value: any, indent?: number): string => {
    if (value === undefined) {
        return '(undefined)';
    }
    const seen = new WeakSet();
    try {
        const json = JSON.stringify(value, (key, inner) => {
            if (typeof inner === 'object' && inner !== null) {
                if (seen.has(inner)) {
                    return '[circular]';
                }
                seen.add(inner);
            }
            if (typeof inner === 'string') {
                return truncate(inner, MAX_VALUE_LENGTH);
            }
            if (typeof inner === 'function') {
                return '[function]';
            }
            return inner;
        }, indent);
        return json === undefined ? String(value) : json;
    } catch (ex) {
        return '[unserializable: ' + (ex && ex.message) + ']';
    }
};

// Error and its subclasses (MSAL BrowserAuthError, axios AxiosError,
// OfficeAsyncError) keep these on the prototype, so a plain
// JSON.stringify(ex) returns "{}" and the real cause is lost.
const ERROR_FIELDS = [
    'name', 'message', 'stack', 'code', 'errorCode', 'errorMessage', 'subError', 'correlationId',
];

// Axios attaches large objects here; the useful parts are lifted out by hand
const SKIP_OWN_FIELDS = ['response', 'config', 'request'];

export const serializeError = (ex: any): any => {
    if (ex === null || ex === undefined) {
        return { message: '(no error object)' };
    }
    if (typeof ex !== 'object') {
        return { message: String(ex) };
    }

    const out: { [key: string]: any } = {};

    for (const field of ERROR_FIELDS) {
        try {
            const value = ex[field];
            if (value !== undefined && value !== null && value !== '') {
                out[field] = value;
            }
        } catch (readEx) { /* property getters are allowed to throw */ }
    }

    try {
        if (ex.response) {
            out.httpStatus = ex.response.status;
            out.httpBody = ex.response.data;
        }
        if (ex.config && ex.config.url) {
            out.requestUrl = ex.config.url;
        }
    } catch (readEx) { /* not an axios error */ }

    // Anything else the object carries itself
    try {
        for (const key of Object.keys(ex)) {
            if (out[key] === undefined && SKIP_OWN_FIELDS.indexOf(key) < 0) {
                out[key] = ex[key];
            }
        }
    } catch (readEx) { /* exotic object */ }

    if (Object.keys(out).length === 0) {
        out.raw = safeStringify(ex);
    }
    return out;
};

/**
 * True when the host failed the call only because another Office.js call on
 * the item was still running. This is the one condition worth retrying;
 * every other host error is a real failure.
 */
export const isTransientOfficeError = (result: any): boolean =>
    !!(result && result.error && result.error.code === ERROR_CODE_PREVIOUS_CALL_IN_PROGRESS);

/**
 * Turns an Office.AsyncResult failure into the fields support actually needs.
 * The host error carries the real reason (for example 5100 "previous call in
 * progress"), and the old code discarded it entirely.
 */
export const describeOfficeError = (result: any): any => {
    const out: { [key: string]: any } = { asyncStatus: result && result.status };
    const error = result && result.error;
    if (error) {
        out.officeErrorCode = error.code;
        out.officeErrorName = error.name;
        out.officeErrorMessage = error.message;
        if (isTransientOfficeError(result)) {
            out.transient = true;
        }
    } else {
        out.officeErrorMessage = '(host reported a failure without an error object)';
    }
    return out;
};

/**
 * Environment facts that explain most startup failures: host and version,
 * whether an item is present and in compose mode, and whether the webview
 * allows cookies. Every read is guarded because at startup half of the
 * Office object graph may not exist yet.
 */
export const collectHostDiagnostics = (): { [key: string]: any } => {
    const out: { [key: string]: any } = {};

    const read = (name: string, get: () => any) => {
        try {
            const value = get();
            if (value !== undefined && value !== null && value !== '') {
                out[name] = value;
            }
        } catch (ex) {
            out[name] = '(unavailable)';
        }
    };

    read('host', () => Office.context.mailbox.diagnostics.hostName);
    read('hostVersion', () => Office.context.mailbox.diagnostics.hostVersion);
    read('owaView', () => Office.context.mailbox.diagnostics.OWAView);
    read('platform', () => Office.context.platform);
    read('displayLanguage', () => Office.context.displayLanguage);
    read('mailboxApi', () => {
        // Highest supported Mailbox requirement set. saveAsync in compose
        // needs 1.3, and the NAA broker needs a recent host.
        const sets = ['1.15', '1.14', '1.13', '1.12', '1.10', '1.9', '1.8', '1.7', '1.6', '1.5', '1.4', '1.3', '1.1'];
        for (const set of sets) {
            if (Office.context.requirements.isSetSupported('Mailbox', set)) {
                return set;
            }
        }
        return 'below-1.1';
    });
    read('hasItem', () => Office.context.mailbox.item != null);
    read('itemMode', () => {
        const item: any = Office.context.mailbox.item;
        if (item == null) {
            return 'no-item';
        }
        // In compose mode subject is an async accessor object; in read mode
        // it is a plain string
        return typeof item.subject === 'object' ? 'compose' : 'read';
    });
    read('itemType', () => (Office.context.mailbox.item as any).itemType);
    read('online', () => navigator.onLine);
    read('cookiesEnabled', () => navigator.cookieEnabled);
    read('origin', () => window.location.origin);
    read('userAgent', () => navigator.userAgent);

    return out;
};

export const buildStartupDiagnostics = (
    code: string,
    phase: string,
    detail?: any,
    now: () => Date = () => new Date()): StartupDiagnostics => ({
        code,
        phase,
        version: getVersion(),
        at: now().toISOString(),
        host: collectHostDiagnostics(),
        detail,
    });

// Rendered into the technical-details box and posted to the server
export const formatStartupDiagnostics = (diagnostics: StartupDiagnostics): string =>
    truncate(safeStringify(diagnostics, 2), MAX_DIAGNOSTICS_LENGTH);

// Help article explaining every startup error code. Each code has its own
// anchor there; keep the anchors in sync with docs/SIGNINERRORS.html.
export const HELP_ARTICLE_URL = 'https://help.mailmerge365.com/article/117-application-errors';

export const buildHelpLink = (code: string): string =>
    `${HELP_ARTICLE_URL}#${(code || '').toLowerCase()}`;
